import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  message,
  Space,
  Typography,
  Row,
  Col,
  Upload,
  Progress,
  Radio,
} from 'antd';
import {
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  DownloadOutlined,
  StopOutlined,
  FileZipOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import dayjs from 'dayjs';
import { saveAs } from 'file-saver';
import './index.less';

const { Title, Text } = Typography;

const BASE_URL = 'https://ds2.cn/ef';

interface EntityFileType {
  id: number;
  code: string;
  name: string;
  maxStage: number;
  baseHealth: number;
  baseSpeed: number;
  baseDamage: number;
  created: string;
  updated: string;
  instances: EntityFileInstance[];
}

interface EntityFileInstance {
  id: number;
  hash: string;
  battleStage: number;
  userImageUrl: string | null;
  battleResult: string | null;
  created: string;
}

const EntityFileAdmin: React.FC = () => {
  const [types, setTypes] = useState<EntityFileType[]>([]);
  const [loading, setLoading] = useState(false);

  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [generateModalVisible, setGenerateModalVisible] = useState(false);
  const [batchGenerateModalVisible, setBatchGenerateModalVisible] = useState(false);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);

  const [selectedType, setSelectedType] = useState<EntityFileType | null>(null);
  const [uploadData, setUploadData] = useState<Array<{ code: string, name: string, maxStage: number, baseHealth: number, baseSpeed: number, baseDamage: number }>>([]);

  // 下载相关状态
  const [downloading, setDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({ current: 0, total: 0 });
  const [downloadController, setDownloadController] = useState<AbortController | null>(null);

  // 批量生成进度状态
  const [batchProgress, setBatchProgress] = useState({
    currentType: '',
    currentTypeIndex: 0,
    totalTypes: 0,
    status: 'idle' as 'idle' | 'running' | 'paused' | 'completed' | 'error',
    completedTypes: [] as string[],
    failedTypes: [] as { code: string; error: string }[]
  });

  const [typeForm] = Form.useForm();
  const [generateForm] = Form.useForm();
  const [batchGenerateForm] = Form.useForm();

  useEffect(() => {
    fetchTypes();
  }, []);

  const fetchTypes = async () => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/entity-file/types');
      const data = await response.json();
      if (data.success) {
        setTypes(data.data || []);
      } else {
        message.error(data.message || '获取Entity File种类失败');
      }
    } catch (error) {
      message.error('获取Entity File种类失败');
    } finally {
      setLoading(false);
    }
  };

  const createType = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch('/api/v1/entity-file/types', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          code: values.code,
          name: values.name,
          maxStage: values.maxStage || 5,
          baseHealth: values.baseHealth || 100,
          baseSpeed: values.baseSpeed || 50,
          baseDamage: values.baseDamage || 30
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success('创建Entity File种类成功');
        setTypeModalVisible(false);
        typeForm.resetFields();
        fetchTypes();
      } else {
        message.error(data.message || '创建Entity File种类失败');
      }
    } catch (error) {
      message.error('创建Entity File种类失败');
    } finally {
      setLoading(false);
    }
  };

  const generateInstances = async (values: any) => {
    setLoading(true);
    try {
      const response = await fetch(`/api/v1/entity-file/types/${selectedType?.id}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          quantity: values.quantity
        })
      });

      const data = await response.json();
      if (data.success) {
        message.success(`成功生成 ${values.quantity} 个Entity File实例`);
        setGenerateModalVisible(false);
        generateForm.resetFields();
        fetchTypes();

        if (data.data && data.data.instances && data.data.instances.length > 0) {
          exportHashList(data.data.instances, data.data.code);
        }
      } else {
        message.error(data.message || '生成Entity File实例失败');
      }
    } catch (error) {
      message.error('生成Entity File实例失败');
    } finally {
      setLoading(false);
    }
  };

  // 取消下载
  const cancelDownload = () => {
    if (downloadController) {
      downloadController.abort();
      setDownloadController(null);
    }
    setDownloading(false);
    setDownloadProgress({ current: 0, total: 0 });
    message.info('下载已取消');
  };

  // 分批下载文件
  const downloadInBatches = async (files: { code: string, content: string }[], batchSize = 5, delay = 1000) => {
    const controller = new AbortController();
    setDownloadController(controller);
    setDownloading(true);
    setDownloadProgress({ current: 0, total: files.length });

    try {
      for (let i = 0; i < files.length; i += batchSize) {
        if (controller.signal.aborted) {
          throw new Error('Download cancelled');
        }

        const batch = files.slice(i, i + batchSize);

        // 并行下载当前批次的文件
        await Promise.all(batch.map(async (file, index) => {
          if (controller.signal.aborted) return;

          const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
          const blob = new Blob([BOM, file.content], { type: 'text/plain;charset=utf-8' });
          const fileName = `code_${file.code}.txt`;
          saveAs(blob, fileName);

          // 更新进度
          setDownloadProgress(prev => ({ ...prev, current: i + index + 1 }));
        }));

        // 批次间延迟，避免浏览器阻塞
        if (i + batchSize < files.length && !controller.signal.aborted) {
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }

      if (!controller.signal.aborted) {
        message.success(`成功下载 ${files.length} 个文件`);
      }
    } catch (error: any) {
      if (error.message !== 'Download cancelled') {
        message.error('下载过程中出现错误');
        console.error('Download error:', error);
      }
    } finally {
      setDownloading(false);
      setDownloadController(null);
      setDownloadProgress({ current: 0, total: 0 });
    }
  };

  // ZIP打包下载
  const downloadAsZip = async (files: { code: string, content: string }[]) => {
    try {
      setLoading(true);

      // 动态导入JSZip
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();

      // 添加文件到ZIP
      files.forEach(file => {
        zip.file(`code_${file.code}.txt`, `\ufeff${file.content}`, { binary: false });
      });

      // 生成ZIP文件
      const content = await zip.generateAsync({ type: 'blob' });
      const fileName = `entity_file_batch_${dayjs().format('YYYYMMDD_HHmmss')}.zip`;
      saveAs(content, fileName);

      message.success(`成功生成ZIP文件包含 ${files.length} 个文件`);
    } catch (error) {
      message.error('生成ZIP文件失败');
      console.error('ZIP generation error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 带重试的请求函数
  const fetchWithRetry = async (
    url: string,
    options: RequestInit,
    maxRetries = 3,
    retryDelay = 1000
  ): Promise<Response> => {
    let lastError: Error | null = null;

    for (let i = 0; i <= maxRetries; i++) {
      try {
        const response = await fetch(url, options);

        // 如果响应成功，直接返回
        if (response.ok) {
          return response;
        }

        // 如果是客户端错误（4xx），不重试
        if (response.status >= 400 && response.status < 500) {
          return response;
        }

        // 服务器错误（5xx），继续重试
        throw new Error(`Server error: ${response.status}`);
      } catch (error: any) {
        lastError = error;

        // 最后一次尝试失败，抛出错误
        if (i === maxRetries) {
          break;
        }

        // 等待后重试，使用指数退避
        const delay = retryDelay * Math.pow(2, i);
        console.log(`请求失败，${delay}ms 后重试 (${i + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError || new Error('请求失败');
  };

  // 分批生成：逐个类型生成，避免一次性加载大量数据
  const batchGenerateInstances = async (values: any) => {
    const { quantity, downloadType } = values;

    // 重置进度状态
    setBatchProgress({
      currentType: '',
      currentTypeIndex: 0,
      totalTypes: types.length,
      status: 'running',
      completedTypes: [],
      failedTypes: []
    });

    setLoading(true);

    try {
      const allFileData: { code: string; content: string }[] = [];

      // 逐个类型生成
      for (let i = 0; i < types.length; i++) {
        const type = types[i];

        // 更新当前处理的类型
        setBatchProgress(prev => ({
          ...prev,
          currentType: type.code,
          currentTypeIndex: i + 1
        }));

        try {
          // 使用带重试的请求调用单个类型的生成接口
          const response = await fetchWithRetry(
            `/api/v1/entity-file/types/${type.id}/generate`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ quantity })
            },
            3, // 最多重试3次
            1000 // 初始延迟1秒
          );

          const data = await response.json();

          if (data.success && data.data && data.data.instances) {
            // 生成文件内容
            const content = data.data.instances
              .map((instance: any) => `${BASE_URL}/${instance.hash}`)
              .join('\r\n') + '\r\n';

            allFileData.push({ code: type.code, content });

            // 更新已完成列表
            setBatchProgress(prev => ({
              ...prev,
              completedTypes: [...prev.completedTypes, type.code]
            }));

            // 如果是分批下载模式，立即下载当前文件
            if (downloadType === 'batch') {
              try {
                const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
                const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
                const fileName = `code_${type.code}.txt`;
                saveAs(blob, fileName);

                // 添加小延迟，避免浏览器阻塞
                await new Promise(resolve => setTimeout(resolve, 500));
              } catch (saveError) {
                console.error(`保存文件 ${type.code} 失败:`, saveError);
                // 即使保存失败也继续，数据已经在 allFileData 中
              }
            }
          } else {
            throw new Error(data.message || '生成失败');
          }
        } catch (error: any) {
          // 记录失败的类型，但继续处理其他类型
          const errorMsg = error.message || '未知错误';
          console.error(`生成 ${type.code} 失败:`, error);
          setBatchProgress(prev => ({
            ...prev,
            failedTypes: [...prev.failedTypes, { code: type.code, error: errorMsg }]
          }));
        }
      }

      // 如果选择ZIP方式，现在统一打包下载
      if (downloadType === 'zip' && allFileData.length > 0) {
        try {
          await downloadAsZip(allFileData);
        } catch (zipError) {
          console.error('ZIP打包失败:', zipError);
          message.error('ZIP打包失败，但数据已生成');
        }
      }

      // 完成后更新状态
      setBatchProgress(prev => ({ ...prev, status: 'completed' }));

      // 计算成功和失败的数量
      const successCount = allFileData.length;
      const failCount = types.length - successCount;

      if (failCount > 0) {
        message.warning(`批量生成完成：成功 ${successCount} 个，失败 ${failCount} 个`);
      } else {
        message.success(`批量生成成功：共 ${successCount} 个文件`);
      }

      // 刷新数据
      fetchTypes();

      // 延迟关闭弹窗，让用户看到完成状态
      setTimeout(() => {
        if (batchProgress.status === 'completed') {
          setBatchGenerateModalVisible(false);
          batchGenerateForm.resetFields();
          setBatchProgress({
            currentType: '',
            currentTypeIndex: 0,
            totalTypes: 0,
            status: 'idle',
            completedTypes: [],
            failedTypes: []
          });
        }
      }, 2000);

    } catch (error) {
      console.error('批量生成失败:', error);
      message.error('批量生成失败');
      setBatchProgress(prev => ({ ...prev, status: 'error' }));
    } finally {
      setLoading(false);
    }
  };

  const exportHashList = (instances: any[], code: string) => {
    const content = instances.map(instance => `${BASE_URL}/${instance.hash}`).join('\r\n') + '\r\n';

    const BOM = new Uint8Array([0xEF, 0xBB, 0xBF]);
    const blob = new Blob([BOM, content], { type: 'text/plain;charset=utf-8' });
    const fileName = `code_${code}.txt`;
    saveAs(blob, fileName);
  };

  const handleFileUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const validData: Array<{ code: string, name: string, maxStage: number, baseHealth: number, baseSpeed: number, baseDamage: number }> = [];

        // 跳过标题行，从第二行开始处理
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i] as any[];
          if (row && row[0] && row[1]) {
            const code = String(row[0]).trim();
            const name = String(row[1]).trim();
            const maxStage = row[2] ? Number(row[2]) : 5;
            const baseHealth = row[3] ? Number(row[3]) : 100;
            const baseSpeed = row[4] ? Number(row[4]) : 50;
            const baseDamage = row[5] ? Number(row[5]) : 30;

            if (code && name) {
              validData.push({ code, name, maxStage, baseHealth, baseSpeed, baseDamage });
            }
          }
        }

        setUploadData(validData);
        message.success(`成功导入${validData.length}条Entity File种类数据`);
      } catch (error) {
        console.error('解析文件时出错:', error);
        message.error('解析表格文件失败，请检查文件格式');
      }
    };
    reader.readAsArrayBuffer(file);
    return false; // 阻止自动上传
  };

  const uploadToServer = async () => {
    if (uploadData.length === 0) {
      message.warning('没有可上传的数据');
      return;
    }

    setLoading(true);
    let successCount = 0;
    let failCount = 0;

    try {
      for (const item of uploadData) {
        try {
          const response = await fetch('/api/v1/entity-file/types', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              code: item.code,
              name: item.name,
              maxStage: item.maxStage || 5,
              baseHealth: item.baseHealth || 100,
              baseSpeed: item.baseSpeed || 50,
              baseDamage: item.baseDamage || 30
            })
          });

          const data = await response.json();
          if (data.success) {
            successCount++;
          } else {
            failCount++;
            console.error(`创建失败 (编号: ${item.code}):`, data.message);
          }
        } catch (error) {
          failCount++;
          console.error(`创建失败 (编号: ${item.code}):`, error);
        }
      }

      message.success(`批量创建完成: 成功 ${successCount} 个, 失败 ${failCount} 个`);

      if (successCount > 0) {
        fetchTypes(); // 刷新数据
        setUploadModalVisible(false);
        setUploadData([]);
      }
    } catch (error) {
      message.error('批量创建失败');
      console.error('批量创建错误:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const templateData = [
      ['编号', '名称', '最高阶段', '基础血量', '基础速度', '基础伤害'],
      ['EF001', '火焰龙', 5, 150, 60, 40],
      ['EF002', '冰霜巨人', 5, 200, 30, 50],
      ['EF003', '雷电法师', 5, 100, 80, 35]
    ];

    const ws = XLSX.utils.aoa_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Entity File种类模板');

    // 设置列宽
    ws['!cols'] = [{ wch: 10 }, { wch: 20 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];

    XLSX.writeFile(wb, 'Entity File种类导入模板.xlsx');
    message.success('模板下载成功');
  };

  const typeColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '编号',
      dataIndex: 'code',
      key: 'code',
      width: 100,
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
      width: 120,
    },
    {
      title: '最高阶段',
      dataIndex: 'maxStage',
      key: 'maxStage',
      width: 90,
    },
    {
      title: '基础属性',
      key: 'baseStats',
      width: 150,
      render: (record: EntityFileType) => (
        <Text style={{ fontSize: '12px' }}>
          HP:{record.baseHealth} SPD:{record.baseSpeed} DMG:{record.baseDamage}
        </Text>
      ),
    },
    {
      title: '实例数量',
      key: 'instanceCount',
      width: 90,
      render: (record: EntityFileType) => (
        <Text>{record.instances?.length || 0}</Text>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created',
      key: 'created',
      width: 160,
      render: (time: string) => time ? dayjs(time).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (record: EntityFileType) => (
        <Space size="middle">
          <Button
            type="primary"
            size="small"
            icon={<PlusOutlined />}
            onClick={() => {
              setSelectedType(record);
              setGenerateModalVisible(true);
            }}
          >
            生成实例
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div className="entity-file-admin">
      <Title level={2}>Entity File管理</Title>

      <Card>
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setTypeModalVisible(true)}
            >
              新增Entity File种类
            </Button>
          </Col>
          <Col>
            <Button
              type="default"
              icon={<UploadOutlined />}
              onClick={() => setUploadModalVisible(true)}
            >
              批量导入
            </Button>
          </Col>
          <Col>
            <Button
              type="default"
              onClick={() => setBatchGenerateModalVisible(true)}
            >
              批量生成
            </Button>
          </Col>
          <Col>
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              下载模板
            </Button>
          </Col>
          <Col>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchTypes}
              loading={loading}
            >
              刷新数据
            </Button>
          </Col>
        </Row>

        <Table
          dataSource={types}
          columns={typeColumns}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: false,
            showQuickJumper: false
          }}
          scroll={{ x: 'max-content' }}
          size="middle"
          bordered={false}
          showHeader={true}
        />
      </Card>

      <Modal
        title="新增Entity File种类"
        open={typeModalVisible}
        onCancel={() => {
          setTypeModalVisible(false);
          typeForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={typeForm}
          layout="vertical"
          onFinish={createType}
          initialValues={{
            maxStage: 5,
            baseHealth: 100,
            baseSpeed: 50,
            baseDamage: 30
          }}
        >
          <Form.Item
            name="code"
            label="编号"
            rules={[{ required: true, message: '请输入编号' }]}
          >
            <Input placeholder="请输入编号（如：EF001）" />
          </Form.Item>

          <Form.Item
            name="name"
            label="名称"
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input placeholder="请输入Entity File名称" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="maxStage"
                label="最高阶段"
                rules={[{ required: true, message: '请输入最高阶段' }]}
              >
                <InputNumber min={1} max={10} placeholder="最高阶段" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="baseHealth"
                label="基础血量"
                rules={[{ required: true, message: '请输入基础血量' }]}
              >
                <InputNumber min={1} placeholder="基础血量" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="baseSpeed"
                label="基础速度"
                rules={[{ required: true, message: '请输入基础速度' }]}
              >
                <InputNumber min={1} placeholder="基础速度" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="baseDamage"
                label="基础伤害"
                rules={[{ required: true, message: '请输入基础伤害' }]}
              >
                <InputNumber min={1} placeholder="基础伤害" style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                创建
              </Button>
              <Button
                onClick={() => {
                  setTypeModalVisible(false);
                  typeForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`生成Entity File实例 - 编号: ${selectedType?.code}`}
        open={generateModalVisible}
        onCancel={() => {
          setGenerateModalVisible(false);
          generateForm.resetFields();
        }}
        footer={null}
      >
        <Form
          form={generateForm}
          layout="vertical"
          onFinish={generateInstances}
        >
          <Form.Item
            name="quantity"
            label="生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber min={1} max={10000} placeholder="请输入生成数量" style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
              >
                生成并导出
              </Button>
              <Button
                onClick={() => {
                  setGenerateModalVisible(false);
                  generateForm.resetFields();
                }}
              >
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量生成Entity File实例"
        open={batchGenerateModalVisible}
        onCancel={() => {
          if (loading && batchProgress.status === 'running') {
            Modal.confirm({
              title: '确认取消',
              content: '批量生成正在进行中，确定要取消吗？',
              onOk: () => {
                setBatchGenerateModalVisible(false);
                batchGenerateForm.resetFields();
                setBatchProgress(prev => ({ ...prev, status: 'idle' }));
              }
            });
          } else {
            setBatchGenerateModalVisible(false);
            batchGenerateForm.resetFields();
          }
        }}
        footer={null}
        width={600}
      >
        <Form
          form={batchGenerateForm}
          layout="vertical"
          onFinish={batchGenerateInstances}
        >
          <div style={{ marginBottom: 16 }}>
            <Text type="secondary">
              将为所有Entity File种类（共 {types.length} 个）各生成指定数量的实例，
              逐个类型依次生成和下载，避免一次性加载大量数据导致浏览器卡顿。
            </Text>
          </div>

          <Form.Item
            name="quantity"
            label="每种Entity File生成数量"
            rules={[{ required: true, message: '请输入生成数量' }]}
          >
            <InputNumber
              min={1}
              max={10000}
              placeholder="请输入每种Entity File生成的数量"
              style={{ width: '100%' }}
              disabled={loading}
            />
          </Form.Item>

          <Form.Item
            name="downloadType"
            label="下载方式"
            initialValue="batch"
            rules={[{ required: true, message: '请选择下载方式' }]}
          >
            <Radio.Group disabled={loading}>
              <Radio value="batch">即时下载（推荐）</Radio>
              <Radio value="zip">ZIP打包下载</Radio>
            </Radio.Group>
          </Form.Item>

          <div style={{ marginBottom: 16 }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              • 即时下载：每生成一个类型立即下载，内存占用小，支持断点续传<br />
              • ZIP打包：生成完所有数据后打包成一个ZIP文件，数据量大时内存占用较高
            </Text>
          </div>

          {/* 批量生成进度显示 */}
          {batchProgress.status !== 'idle' && (
            <div style={{ marginBottom: 16, padding: 16, background: '#f5f5f5', borderRadius: 8 }}>
              <div style={{ marginBottom: 12 }}>
                <Text strong>生成进度</Text>
              </div>

              <div style={{ marginBottom: 8 }}>
                <Text>
                  {batchProgress.status === 'running' && `正在处理: ${batchProgress.currentType}`}
                  {batchProgress.status === 'completed' && '✓ 批量生成已完成'}
                  {batchProgress.status === 'error' && '✗ 批量生成出错'}
                </Text>
              </div>

              <Progress
                percent={batchProgress.totalTypes > 0
                  ? Math.round((batchProgress.currentTypeIndex / batchProgress.totalTypes) * 100)
                  : 0}
                status={
                  batchProgress.status === 'completed' ? 'success' :
                  batchProgress.status === 'error' ? 'exception' :
                  'active'
                }
                format={() => `${batchProgress.currentTypeIndex}/${batchProgress.totalTypes}`}
              />

              <div style={{ marginTop: 12, fontSize: '12px' }}>
                <Text type="success">已完成: {batchProgress.completedTypes.length} 个</Text>
                {batchProgress.failedTypes.length > 0 && (
                  <>
                    <br />
                    <Text type="danger">失败: {batchProgress.failedTypes.length} 个</Text>
                    <div style={{ marginTop: 8, maxHeight: 100, overflow: 'auto' }}>
                      {batchProgress.failedTypes.map((item, idx) => (
                        <div key={idx} style={{ color: '#ff4d4f' }}>
                          • {item.code}: {item.error}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                disabled={batchProgress.status === 'running'}
                icon={loading ? undefined : <DownloadOutlined />}
              >
                {loading ? '生成中...' : '开始批量生成'}
              </Button>

              <Button
                onClick={() => {
                  if (loading && batchProgress.status === 'running') {
                    Modal.confirm({
                      title: '确认取消',
                      content: '批量生成正在进行中，确定要关闭吗？已生成的数据不会丢失。',
                      onOk: () => {
                        setBatchGenerateModalVisible(false);
                        batchGenerateForm.resetFields();
                      }
                    });
                  } else {
                    setBatchGenerateModalVisible(false);
                    batchGenerateForm.resetFields();
                    if (batchProgress.status !== 'idle') {
                      setBatchProgress({
                        currentType: '',
                        currentTypeIndex: 0,
                        totalTypes: 0,
                        status: 'idle',
                        completedTypes: [],
                        failedTypes: []
                      });
                    }
                  }
                }}
                disabled={loading && batchProgress.status === 'running'}
              >
                关闭
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="批量导入Entity File种类"
        open={uploadModalVisible}
        onCancel={() => {
          setUploadModalVisible(false);
          setUploadData([]);
        }}
        footer={null}
        width={700}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Text type="secondary">
              请先下载模板，按照模板格式填写数据后再上传。支持 .xlsx、.xls 格式文件。
            </Text>
          </div>

          <Space>
            <Upload
              accept=".xlsx,.xls"
              beforeUpload={handleFileUpload}
              showUploadList={false}
            >
              <Button icon={<UploadOutlined />}>选择文件</Button>
            </Upload>

            <Button
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
            >
              下载模板
            </Button>
          </Space>

          {uploadData.length > 0 && (
            <>
              <div>
                <Text strong>已导入数据预览 ({uploadData.length} 条)</Text>
              </div>

              <Table
                dataSource={uploadData.map((item, index) => ({ ...item, key: index }))}
                columns={[
                  {
                    title: '编号',
                    dataIndex: 'code',
                    key: 'code',
                    width: 100,
                  },
                  {
                    title: '名称',
                    dataIndex: 'name',
                    key: 'name',
                    width: 120,
                  },
                  {
                    title: '最高阶段',
                    dataIndex: 'maxStage',
                    key: 'maxStage',
                    width: 90,
                  },
                  {
                    title: '基础血量',
                    dataIndex: 'baseHealth',
                    key: 'baseHealth',
                    width: 90,
                  },
                  {
                    title: '基础速度',
                    dataIndex: 'baseSpeed',
                    key: 'baseSpeed',
                    width: 90,
                  },
                  {
                    title: '基础伤害',
                    dataIndex: 'baseDamage',
                    key: 'baseDamage',
                    width: 90,
                  }
                ]}
                pagination={{ pageSize: 10 }}
                size="small"
                scroll={{ x: 'max-content' }}
                style={{ maxHeight: '300px', overflow: 'auto' }}
              />

              <Space>
                <Button
                  type="primary"
                  onClick={uploadToServer}
                  loading={loading}
                >
                  确认导入到服务器
                </Button>
                <Button
                  onClick={() => {
                    setUploadModalVisible(false);
                    setUploadData([]);
                  }}
                >
                  取消
                </Button>
              </Space>
            </>
          )}
        </Space>
      </Modal>
    </div>
  );
};

export default EntityFileAdmin;
